import { FC, MouseEventHandler, useCallback, useMemo, useRef } from 'react';

import { getBadgeCountValue } from 'utils/chatik/getBadgeCountValue';
import { formatCost, isAccountStatusReadOnly } from 'utils/clickme';
import { CREATE_MENU_PAGE_ROUTE, TOP_UP_PAGE_ROUTE, VACANCIES_LINK } from 'utils/constants/app';
import { NON_BREAKING_SPACE } from 'utils/constants/common';

import headerButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/header/header_button_click';
import {
    Badge,
    Button,
    Card,
    HSpacing,
    HSpacingContainer,
    Link,
    Text,
    useBreakpoint,
    VSpacing,
    VSpacingContainer,
} from '@hh.ru/magritte-ui';
import { BarsOutlinedSize24, ChevronRightOutlinedSize16, CrossOutlinedSize24 } from '@hh.ru/magritte-ui/icon';
import { Link as SPALink } from '@hh.ru/redux-spa-middleware';

import useOnOffState from 'hooks/common/useOnOffState';
import useTrls from 'hooks/translation/useTrls';
import { useDashboardRoute } from 'hooks/useDashboardRoute';

import FormatMoney from 'components/FormatMoney';

import { Logo, LogoSize } from 'components/Logo/Logo';

import { setIsHelpModalVisible, setUserDropdownOpen } from 'store/autoReducers';
import { useDispatch, useSelector } from 'store/utils';

import styles from 'components/Header/HeaderNav/HeaderNav.module.less';

const TrlKeys = {
    campaigns: 'Header.HeaderNav.ads',
    vacancies: 'Header.HeaderNav.vacancies',
    help: 'Header.HeaderNav.help',
    createCampaign: 'Header.HeaderNav.createCampaign',
    balance: 'Header.HeaderNav.balance',
    bonusBalance: 'Header.HeaderNav.bonusBalance',
};

export const HeaderNav: FC = () => {
    const { balance, bonusBalance } = useSelector(({ balance }) => balance);
    const unreadSupportCount = useSelector((state) => state.chatikUnreadSupportCount);
    const badgeValue = useMemo(() => getBadgeCountValue(unreadSupportCount), [unreadSupportCount]);

    const currentAccountStatus = useSelector(({ user }) => user.user?.currentAccount.status);
    const isReadOnly = isAccountStatusReadOnly(currentAccountStatus);

    const [isExpanded, setIsExpandedOn, setIsExpandedOff] = useOnOffState(false);

    const dispatch = useDispatch();
    const trls = useTrls();
    const { isMobile } = useBreakpoint();
    const dashboardRoute = useDashboardRoute();

    const contentRef = useRef<HTMLDivElement>(null);

    const handleOverlayClick = useCallback<MouseEventHandler<HTMLDivElement>>(
        (event) => {
            if (event.target instanceof Element && contentRef.current) {
                if (!contentRef.current.contains(event.target) && event.target !== contentRef.current) {
                    setIsExpandedOff();
                }
            }
        },
        [setIsExpandedOff]
    );

    const navContent = (
        <>
            <Link
                className={styles.colorOverride}
                Element={SPALink}
                to={dashboardRoute}
                mode="secondary"
                style="constant"
                onClick={() => {
                    dispatch(setUserDropdownOpen(false));
                    setIsExpandedOff();
                    headerButtonClick({ buttonName: 'my_campaigns_header' });
                }}
                data-qa="navigation_my_campaigns"
            >
                <Text typography="label-2-regular">{trls(TrlKeys.campaigns)}</Text>
            </Link>
            <Link
                className={styles.colorOverride}
                href={VACANCIES_LINK}
                target="_blank"
                mode="secondary"
                style="constant"
                onClick={() => {
                    dispatch(setUserDropdownOpen(false));
                    headerButtonClick({ buttonName: 'my_vacancies_header' });
                }}
                data-qa="navigation_my_vacancies"
            >
                <Text typography="label-2-regular">{trls(TrlKeys.vacancies)}</Text>
            </Link>
            <Link
                className={styles.colorOverride}
                Element="button"
                onClick={() => {
                    dispatch([setIsHelpModalVisible(true), setUserDropdownOpen(false)]);
                    setIsExpandedOff();
                    headerButtonClick({ buttonName: 'help_header' });
                }}
                data-qa="navigation_help_header"
            >
                <Text typography="label-2-regular">
                    <div className={styles.helpWrapper}>
                        {trls(TrlKeys.help)}
                        {unreadSupportCount > 0 && (
                            <>
                                <HSpacing default={8} />
                                <Badge size="small">{badgeValue}</Badge>
                            </>
                        )}
                    </div>
                </Text>
            </Link>
        </>
    );

    return isMobile ? (
        <>
            <div className={styles.colorOverride}>
                <BarsOutlinedSize24 initialColor="constant-secondary" onClick={setIsExpandedOn} />
            </div>
            {isExpanded && (
                <div onClick={handleOverlayClick} className={styles.overlay}>
                    <Card
                        ref={contentRef}
                        style="constant"
                        stretched
                        borderBottomLeftRadius={24}
                        borderBottomRightRadius={24}
                        padding={0}
                    >
                        <VSpacingContainer default={24}>
                            <div className={styles.header}>
                                <Logo size={isMobile ? LogoSize.Small : undefined} />
                                <CrossOutlinedSize24
                                    initialColor="constant"
                                    highlightedColor="constant-secondary"
                                    onClick={setIsExpandedOff}
                                />
                            </div>
                            <div className={styles.content}>
                                <VSpacingContainer default={20}>{navContent}</VSpacingContainer>
                            </div>
                            <div className={styles.content}>
                                <Card
                                    borderRadius={16}
                                    stretched
                                    style="constant-secondary"
                                    padding={16}
                                    Element={SPALink}
                                    to={TOP_UP_PAGE_ROUTE}
                                    onClick={setIsExpandedOff}
                                >
                                    <div className={styles.balanceCard}>
                                        <div className={styles.balanceWrapper}>
                                            <Text typography="label-3-regular" style="constant-secondary">
                                                {trls(TrlKeys.balance)}
                                            </Text>
                                            <Text typography="subtitle-1-semibold" style="constant">
                                                <FormatMoney groupSeparator={NON_BREAKING_SPACE}>{balance}</FormatMoney>
                                            </Text>
                                        </div>
                                        <div className={styles.balanceWrapper}>
                                            <Text typography="label-3-regular" style="constant-secondary">
                                                {trls(TrlKeys.bonusBalance)}
                                            </Text>
                                            <Text typography="subtitle-1-semibold" style="constant">
                                                {formatCost(bonusBalance)}
                                            </Text>
                                        </div>
                                        <ChevronRightOutlinedSize16 initialColor="constant" />
                                    </div>
                                </Card>
                                {isReadOnly && <VSpacing default={12} />}
                            </div>

                            {!isReadOnly && (
                                <div className={styles.footer}>
                                    <Button
                                        mode="secondary"
                                        style="constant"
                                        Element={SPALink}
                                        to={CREATE_MENU_PAGE_ROUTE}
                                        stretched
                                        onClick={setIsExpandedOff}
                                    >
                                        {trls(TrlKeys.createCampaign)}
                                    </Button>
                                </div>
                            )}
                        </VSpacingContainer>
                    </Card>
                </div>
            )}
        </>
    ) : (
        <HSpacingContainer default={16}>{navContent}</HSpacingContainer>
    );
};
